/**
 * Viewer usage meter: counts what the viewer pages fetch, so the admin cost
 * analysis can price viewing from measurements instead of per-session guesses.
 *
 * This window's fetch and XMLHttpRequest are wrapped once at start-up; the
 * viewers' iframes run the same meter (public/usage-meter.js) and post their
 * totals here; the Scene engine adds its worker-side octree fetches to its
 * frame's meter; 360 videos are estimated from what the <video> element has
 * buffered. A request is counted when it is made (CloudFront bills cancelled
 * requests too), its bytes from the response's Content-Length.
 *
 * While a viewer route is open (useViewerUsage), the counts since the last
 * report go to POST /api/usage/viewer once a minute and when the page closes.
 */

export type Channel = 'cdn' | 's3' | 'proxy' | 'esri' | 'google' | 'osm';
export interface Traffic { requests: number; bytes: number }
export interface Counters { cdn: Traffic; s3: Traffic; proxy: Traffic; tiles: { esri: number; google: number; osm: number } }

export const zeroCounters = (): Counters => ({
  cdn: { requests: 0, bytes: 0 },
  s3: { requests: 0, bytes: 0 },
  proxy: { requests: 0, bytes: 0 },
  tiles: { esri: 0, google: 0, osm: 0 },
});

// Same-origin endpoints that stream file bytes out of the app server: our
// egress like S3's. Everything else same-origin (GraphQL, auth) is ignored.
const PROXY_PATHS = [
  /^\/api\/potree\/files\//,
  /^\/api\/panoramas\/[^/]+\/file\//,
  /^\/api\/ortho\/[^/]+\/file\//,
  /^\/api\/files\/[^/]+\/stream/,
  /^\/api\/dwg2d\/(file|tab|points)\//,
  /^\/api\/ifc3d\/file\//,
];

// Keep in step with classify() in public/usage-meter.js.
export function classifyUrl(raw: string, origin: string): Channel | null {
  let u: URL;
  try { u = new URL(raw, origin); } catch { return null; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  if (u.origin === origin) return PROXY_PATHS.some((r) => r.test(u.pathname)) ? 'proxy' : null;
  const host = u.hostname;
  if (host.endsWith('.cloudfront.net') || host.startsWith('cdn.')) return 'cdn';
  // Only signed URLs are ours: public open-data buckets (terrain tiles) are not.
  if (host.endsWith('.amazonaws.com') && /[?&]X-Amz-(Signature|Credential)=/i.test(u.search)) return 's3';
  if (host.endsWith('.arcgis.com') || host.endsWith('.arcgisonline.com')) return 'esri';
  if (host === 'tile.googleapis.com' && /\/2dtiles\/\d+\/\d+\/\d+/.test(u.pathname)) return 'google';
  if (host.endsWith('tile.openstreetmap.org')) return 'osm';
  return null;
}

export function record(c: Counters, channel: Channel, bytes: number, requests = 1): void {
  if (channel === 'esri' || channel === 'google' || channel === 'osm') {
    c.tiles[channel] += requests;
    return;
  }
  c[channel].requests += requests;
  c[channel].bytes += Math.max(0, bytes || 0);
}

export function addCounters(into: Counters, d: Counters): void {
  for (const ch of ['cdn', 's3', 'proxy'] as const) {
    into[ch].requests += d[ch].requests;
    into[ch].bytes += d[ch].bytes;
  }
  for (const t of ['esri', 'google', 'osm'] as const) into.tiles[t] += d.tiles[t];
}

// cur − prev, field by field; a field that went down (a reloaded frame
// starting again from zero) counts from zero.
export function diffCounters(cur: Counters, prev: Counters): Counters {
  const d = zeroCounters();
  const sub = (a: number, b: number) => (a >= b ? a - b : a);
  for (const ch of ['cdn', 's3', 'proxy'] as const) {
    d[ch].requests = sub(cur[ch].requests, prev[ch].requests);
    d[ch].bytes = sub(cur[ch].bytes, prev[ch].bytes);
  }
  for (const t of ['esri', 'google', 'osm'] as const) d.tiles[t] = sub(cur.tiles[t], prev.tiles[t]);
  return d;
}

export const isZeroCounters = (c: Counters) =>
  !c.cdn.requests && !c.s3.requests && !c.proxy.requests && !c.tiles.esri && !c.tiles.google && !c.tiles.osm;

const num = (v: unknown) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : 0);

export function readCounters(raw: unknown): Counters | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, Record<string, unknown>>;
  const c = zeroCounters();
  for (const ch of ['cdn', 's3', 'proxy'] as const) {
    c[ch].requests = num(r[ch]?.requests);
    c[ch].bytes = num(r[ch]?.bytes);
  }
  for (const t of ['esri', 'google', 'osm'] as const) c.tiles[t] = num(r.tiles?.[t]);
  return c;
}

export interface ViewerRoute { kind: string; projectId?: string; shareToken?: string; fileId?: string }

const ROUTE_KIND: Record<string, string> = {
  scene: 'scene',
  'viewer-splat': 'splat',
  'viewer-ortho': 'ortho',
  'viewer-360': 'video360',
  'viewer-2d': '2d',
  'viewer-2d-v2': '2d',
  'viewer-3d': '3d',
  'viewer-3d-v2': '3d',
  'viewer-pdf': 'pdf',
  'viewer-cloud': 'cloud',
  'viewer-cloud-v2': 'cloud',
};

/** The viewer a path opens, or null for a page that is not a viewer. */
export function viewerRouteInfo(pathname: string): ViewerRoute | null {
  const parts = pathname.split('/').filter(Boolean);
  const kindOf = (seg?: string) => (seg ? ROUTE_KIND[seg] ?? (seg.startsWith('viewer-') ? 'other' : null) : null);
  if (parts[0] === 'projects' && parts[1]) {
    const kind = kindOf(parts[2]);
    return kind ? { kind, projectId: parts[1], ...(parts[3] && { fileId: parts[3] }) } : null;
  }
  if (parts[0] === 'share' && parts[1] && parts[1] !== 'release-notes') {
    if (!parts[2]) return { kind: 'shared', shareToken: parts[1] };
    const kind = kindOf(parts[2]);
    return kind ? { kind, shareToken: parts[1], ...(parts[3] && { fileId: parts[3] }) } : null;
  }
  return null;
}

// ── Meter state (this window + the iframes that report to it) ───────────────

const local = zeroCounters();
const frames = new Map<string, Counters>();
const mediaRates = new Map<string, number>();
const mediaSeen = new Map<string, { bytes: number; ranges: Set<number> }>();
let installed = false;

export function totalCounters(): Counters {
  const t = zeroCounters();
  addCounters(t, local);
  for (const f of frames.values()) addCounters(t, f);
  return t;
}

/** Bytes a second of a video URL, so its buffered seconds can be priced. */
export function registerMediaRate(url: string, bytesPerSecond: number): void {
  if (url && bytesPerSecond > 0) mediaRates.set(url, bytesPerSecond);
}

// Players that never put their <video> in the page (Photo Sphere Viewer's
// video adapter keeps it detached) hand it over here.
const trackedMedia = new Set<HTMLMediaElement>();

export function trackMedia(el: HTMLMediaElement): void {
  trackedMedia.add(el);
}

// A media element fetches with Range requests the page cannot see; what it
// has buffered × the rendition's bitrate is what it downloaded, and every new
// buffered range is one more request.
export function sampleMedia(): void {
  const elements = new Set<HTMLMediaElement>([...Array.from(document.querySelectorAll('video')), ...trackedMedia]);
  for (const v of elements) {
    const src = v.currentSrc || v.src;
    if (!src) { trackedMedia.delete(v); continue; }
    const rate = mediaRates.get(src);
    const ch = rate ? classifyUrl(src, window.location.origin) : null;
    if (!rate || !ch) continue;
    const seen = mediaSeen.get(src) ?? { bytes: 0, ranges: new Set<number>() };
    let seconds = 0;
    let newRanges = 0;
    for (let i = 0; i < v.buffered.length; i++) {
      seconds += v.buffered.end(i) - v.buffered.start(i);
      const key = Math.round(v.buffered.start(i));
      if (!seen.ranges.has(key)) { seen.ranges.add(key); newRanges++; }
    }
    const bytes = seconds * rate;
    if (bytes > seen.bytes || newRanges) {
      record(local, ch, Math.max(0, bytes - seen.bytes), newRanges);
      seen.bytes = Math.max(seen.bytes, bytes);
    }
    mediaSeen.set(src, seen);
  }
}

const urlOf = (input: RequestInfo | URL) =>
  (typeof input === 'string' ? input : input instanceof URL ? input.href : input.url);

export function installUsageMeter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const origin = window.location.origin;

  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const ch = method === 'GET' ? classifyUrl(urlOf(input), origin) : null;
    if (!ch) return origFetch(input, init);
    try {
      const res = await origFetch(input, init);
      record(local, ch, Number(res.headers.get('content-length')) || 0);
      return res;
    } catch (err) {
      record(local, ch, 0);
      throw err;
    }
  };

  const open = XMLHttpRequest.prototype.open;
  const send = XMLHttpRequest.prototype.send;
  const xhrChannel = new WeakMap<XMLHttpRequest, Channel>();
  XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    const ch = String(method).toUpperCase() === 'GET' ? classifyUrl(String(url), origin) : null;
    if (ch) xhrChannel.set(this, ch); else xhrChannel.delete(this);
    return (open as (...a: unknown[]) => void).call(this, method, url, ...rest);
  } as typeof XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
    const ch = xhrChannel.get(this);
    if (ch) {
      this.addEventListener('loadend', (e: ProgressEvent) => { record(local, ch, e.loaded); }, { once: true });
    }
    return send.call(this, body);
  };

  window.addEventListener('message', (e: MessageEvent) => {
    if (e.origin !== origin) return;
    const d = e.data as { type?: string; frameId?: unknown; counters?: unknown } | null;
    if (d?.type !== 'usageMeter' || typeof d.frameId !== 'string') return;
    const c = readCounters(d.counters);
    if (c) frames.set(d.frameId, c);
  });
}

// ── Reporting sessions ───────────────────────────────────────────────────────

const REPORT_MS = 60_000;
const MEDIA_MS = 5_000;

const newSessionId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

export function startViewerSession(route: ViewerRoute): () => void {
  const sessionId = newSessionId();
  const startedAt = new Date().toISOString();
  let last = totalCounters();
  let visibleMs = 0;
  let visibleSince = document.visibilityState === 'visible' ? Date.now() : null;

  const takeVisible = () => {
    const now = Date.now();
    let ms = visibleMs;
    if (visibleSince != null) { ms += now - visibleSince; visibleSince = now; }
    visibleMs = 0;
    return Math.round(ms / 1000);
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      visibleSince = Date.now();
    } else {
      if (visibleSince != null) visibleMs += Date.now() - visibleSince;
      visibleSince = null;
      report();
    }
  };

  const report = () => {
    sampleMedia();
    const now = totalCounters();
    const delta = diffCounters(now, last);
    const visibleSeconds = takeVisible();
    if (isZeroCounters(delta)) { visibleMs += visibleSeconds * 1000; return; }
    last = now;
    const token = localStorage.getItem('geodraft_token');
    void fetch('/api/usage/viewer', {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(!route.shareToken && token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        sessionId,
        kind: route.kind,
        projectId: route.projectId,
        fileId: route.fileId,
        shareToken: route.shareToken,
        startedAt,
        visibleSeconds,
        ...delta,
      }),
    }).catch(() => {});
  };

  const reportTimer = window.setInterval(report, REPORT_MS);
  const mediaTimer = window.setInterval(sampleMedia, MEDIA_MS);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', report);
  return () => {
    window.clearInterval(reportTimer);
    window.clearInterval(mediaTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', report);
    report();
  };
}
